import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Star,
  Clock,
  MapPin,
  Award,
  Send,
  Sparkles,
  User,
  Phone,
  Mail,
  Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OFFICE_PHONE = "(559) 795-5455";
const OFFICE_PHONE_RAW = "5597955455";

/**
 * Caregiver Matching
 * 
 * Step 6 of the Intake Workflow
 * Match and assign caregivers to the care plan, send work orders
 */

export default function CaregiverMatching() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false); // New: track if work order was sent
  const [request, setRequest] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  const [caregivers, setCaregivers] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    fetchData();
  }, [requestId]);

  const fetchData = async () => {
    try {
      // Fetch service request
      const reqRes = await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
        credentials: 'include'
      });
      if (reqRes.ok) {
        const reqData = await reqRes.json();
        setRequest(reqData);
      }

      // Fetch care plan
      const planRes = await fetch(`${API_URL}/api/intake/care-plans?service_request_id=${requestId}&limit=1`, {
        credentials: 'include'
      });
      if (planRes.ok) {
        const planData = await planRes.json();
        if (planData.items && planData.items.length > 0) {
          setCarePlan(planData.items[0]);
        }
      }

      // Fetch available caregivers
      const cgRes = await fetch(`${API_URL}/api/data-entry/caregivers?status=active&limit=100`, {
        credentials: 'include'
      });
      if (cgRes.ok) {
        const cgData = await cgRes.json();
        setCaregivers(cgData.items || cgData || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    if (!request?.client_address || !carePlan) return;

    setLoadingRecommendations(true);
    try {
      const res = await fetch(`${API_URL}/api/data-entry/recommend-caregivers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_address: request.client_address,
          client_city: '', // Would extract from address
          client_state: '',
          care_needs: carePlan.care_needs_summary || '',
        })
      });

      if (res.ok) {
        const data = await res.json();
        setRecommendations(data.recommendations || []);
      } else {
        // Fallback to showing all caregivers
        setRecommendations(caregivers.map(cg => ({
          ...cg,
          match_score: null,
          match_reasons: ['Available caregiver']
        })));
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setRecommendations(caregivers.map(cg => ({
        ...cg,
        match_score: null,
        match_reasons: ['Available caregiver']
      })));
    } finally {
      setLoadingRecommendations(false);
    }
  };

  useEffect(() => {
    if (request && carePlan && caregivers.length > 0) {
      fetchRecommendations();
    }
  }, [request, carePlan, caregivers]);

  const handleSendWorkOrder = async () => {
    if (!selectedCaregiver) {
      toast.error('Please select a caregiver');
      return;
    }

    if (!carePlan) {
      toast.error('Care plan required');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/work-orders`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          care_plan_id: carePlan.id,
          caregiver_id: selectedCaregiver.id,
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Work order sent to ${selectedCaregiver.first_name}!`);
        setSent(true); // Show the phone call confirmation screen
        
        // Update service request status
        await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'caregiver_assigned' })
        });

        // Don't navigate away - show phone call confirmation
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send work order');
      }
    } catch (err) {
      console.error('Error sending work order:', err);
      toast.error('Failed to send work order');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/organization/data-entry')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to CareHub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0D1B33]">Caregiver Matching</h1>
              <p className="text-sm text-[#64748B]">
                Step 6 of 6 — {request?.client_name}
              </p>
            </div>
          </div>
        </header>

        {/* No Care Plan Warning */}
        {!carePlan && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">Care Plan Required</h3>
                <p className="text-amber-700 mt-1">
                  Please complete the care plan before matching a caregiver.
                </p>
                <Button
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate(`/intake/care-plan/${requestId}`)}
                >
                  Create Care Plan
                </Button>
              </div>
            </div>
          </div>
        )}

        {carePlan && (
          <>
            {/* Care Plan Summary */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-semibold text-[#0D1B33]">Care Plan Summary</h2>
              </div>
              <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Client</p>
                  <p className="font-medium text-[#0D1B33]">{request?.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Weekly Hours</p>
                  <p className="font-medium text-[#0D1B33]">{carePlan.total_hours_per_week || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Start Date</p>
                  <p className="font-medium text-[#0D1B33]">{carePlan.start_date || 'TBD'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wide">Services</p>
                  <p className="font-medium text-[#0D1B33]">
                    {(carePlan.adl_needs?.length || 0) + (carePlan.iadl_needs?.length || 0)} items
                  </p>
                </div>
              </div>
            </div>

            {/* AI Recommendations */}
            <div className="bg-gradient-to-r from-[#176DFF]/10 to-[#29C0FF]/10 rounded-xl border border-[#176DFF]/20 p-4 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#176DFF] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0D1B33]">Leynix Recommendations</h3>
                  <p className="text-sm text-[#64748B]">
                    Caregivers ranked by availability, location, and qualifications
                  </p>
                </div>
              </div>

              {loadingRecommendations ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#176DFF] mr-2" />
                  <span className="text-[#64748B]">Finding best matches...</span>
                </div>
              ) : recommendations.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#64748B] mb-4">
                    No caregivers available. Please add caregivers in CareHub first.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/organization/data-entry')}
                  >
                    Go to CareHub
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {recommendations.slice(0, 5).map((caregiver, idx) => (
                    <CaregiverCard
                      key={caregiver.id}
                      caregiver={caregiver}
                      rank={idx + 1}
                      isSelected={selectedCaregiver?.id === caregiver.id}
                      onSelect={() => setSelectedCaregiver(caregiver)}
                      isBestMatch={idx === 0}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* All Caregivers */}
            {caregivers.length > 0 && (
              <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
                <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                  <h2 className="font-semibold text-[#0D1B33]">All Available Caregivers</h2>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {caregivers.map((caregiver) => (
                      <button
                        key={caregiver.id}
                        onClick={() => setSelectedCaregiver(caregiver)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                          selectedCaregiver?.id === caregiver.id
                            ? 'border-emerald-400 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[#0D1B33]">
                            {caregiver.first_name} {caregiver.last_name}
                          </p>
                          <p className="text-xs text-[#64748B]">{caregiver.email || caregiver.phone || 'No contact'}</p>
                        </div>
                        {selectedCaregiver?.id === caregiver.id && (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Selected Caregiver Preview - Before Sending */}
            {selectedCaregiver && !sent && (
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 mb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-emerald-900">Selected Caregiver</h3>
                    <p className="text-emerald-700 mt-1">
                      <strong>{selectedCaregiver.first_name} {selectedCaregiver.last_name}</strong> will receive a work order 
                      for <strong>{request?.client_name}</strong>.
                    </p>
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-2">
                        <Phone className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <strong>Important:</strong> Caregivers must call the office at <strong>{OFFICE_PHONE}</strong> to 
                          accept or decline this work order. They cannot simply click a button.
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center">
                    <User className="w-6 h-6 text-emerald-700" />
                  </div>
                </div>
              </div>
            )}

            {/* Work Order Sent - Phone Call Confirmation */}
            {sent && selectedCaregiver && (
              <div className="space-y-6">
                <div className="bg-emerald-50 rounded-xl border-2 border-emerald-200 p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-emerald-900">Work Order Sent!</h2>
                  <p className="text-emerald-700 mt-2">
                    {selectedCaregiver.first_name} {selectedCaregiver.last_name} has been notified.
                  </p>
                </div>

                {/* Phone Call Requirement Notice */}
                <div className="bg-[#176DFF]/5 rounded-xl border border-[#176DFF]/20 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#176DFF]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#176DFF]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0D1B33]">Caregiver Must Call to Accept</h3>
                      <p className="text-sm text-[#64748B] mt-1">
                        Per ATOA policy, <strong>{selectedCaregiver.first_name}</strong> must call the office 
                        to officially accept or decline this assignment. Button clicks alone are not accepted.
                      </p>
                      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-sm text-[#64748B]">Office Phone</p>
                        <p className="text-xl font-bold text-[#0D1B33]">{OFFICE_PHONE}</p>
                      </div>
                      <p className="text-xs text-[#64748B] mt-3">
                        Office staff will log the caregiver&apos;s response in the system after the phone call.
                      </p>
                    </div>
                  </div>
                </div>

                {/* What Happens Next */}
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
                  <h4 className="font-semibold text-[#0D1B33] mb-3">Next Steps</h4>
                  <ol className="space-y-3 text-sm">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#176DFF] text-white text-xs font-semibold flex items-center justify-center shrink-0">1</span>
                      <span className="text-[#64748B]">Caregiver receives email notification with work order details</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center shrink-0">2</span>
                      <span className="text-[#64748B]">Caregiver calls office at <strong>{OFFICE_PHONE}</strong> to accept or decline</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center shrink-0">3</span>
                      <span className="text-[#64748B]">Office staff logs the response and schedules first visit if accepted</span>
                    </li>
                  </ol>
                </div>

                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/organization/data-entry')}
                  >
                    Return to CareHub
                  </Button>
                  <Button
                    className="bg-[#176DFF] hover:bg-[#176DFF]/90"
                    onClick={() => navigate('/organization/coverage')}
                  >
                    View Shift Coverage Dashboard
                  </Button>
                </div>
              </div>
            )}

            {/* Actions - Only show if not sent yet */}
            {!sent && (
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/intake/agreement/${requestId}`)}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back to Agreement
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSendWorkOrder}
                  disabled={sending || !selectedCaregiver}
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Work Order
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CaregiverCard({ caregiver, rank, isSelected, onSelect, isBestMatch }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center justify-between p-4 rounded-lg border-2 text-left transition-all ${
        isSelected
          ? 'border-[#176DFF] bg-[#176DFF]/5'
          : 'border-white bg-white hover:border-gray-200'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          isBestMatch ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {isBestMatch ? <Star className="w-4 h-4" /> : rank}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-[#0D1B33]">
              {caregiver.first_name} {caregiver.last_name}
            </p>
            {isBestMatch && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Best Match
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-[#64748B] mt-1">
            {caregiver.match_reasons?.map((reason, i) => (
              <span key={i} className="flex items-center gap-1">
                {reason.includes('Available') && <Clock className="w-3 h-3" />}
                {reason.includes('location') && <MapPin className="w-3 h-3" />}
                {reason.includes('certified') && <Award className="w-3 h-3" />}
                {reason}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {caregiver.match_score && (
          <span className="text-sm font-medium text-[#176DFF] bg-[#176DFF]/10 px-3 py-1 rounded-full">
            {caregiver.match_score}% match
          </span>
        )}
        {isSelected && (
          <CheckCircle2 className="w-5 h-5 text-[#176DFF]" />
        )}
      </div>
    </button>
  );
}
